<template>
  <div class="contact-form">
    <div
      :id="id"
      :class="[{'focus': focusedInput || isFocus, 'error': error, 'maskInp_mini': inputMode === 'mini' }]"
      class="vue-phone-number-input maskInp flex">
      <div
        class="select-country-container">
        <div
          class="country-selector"
          @blur.capture="handleBlur">
          <input
            tabindex="-1"
            id="CountrySelector"
            ref="CountrySelector"
            autocomplete="off"
            :placeholder="callingCode"
            readonly
            name="callingCode"
            @focus="isFocus = true"
            @blur="isFocus = false"
            @keydown="keyboardNav"
            @click.stop="toggleList">
          <div v-if="!countryLock" class="country-selector__toggle" @click.stop="toggleList">
            <svg width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M0.209209 0.183058C0.488155 -0.0610194 0.940416 -0.0610194 1.21936 0.183058L5 3.49112L8.78064 0.183058C9.05958 -0.0610194 9.51184 -0.0610194 9.79079 0.183058C10.0697 0.427136 10.0697 0.822864 9.79079 1.06694L5.50508 4.81694C5.22613 5.06102 4.77387 5.06102 4.49492 4.81694L0.209209 1.06694C-0.0697365 0.822864 -0.0697365 0.427136 0.209209 0.183058Z" fill="#526C82" fill-opacity="0.8"/>
            </svg>
          </div>
          <label
            ref="label"
            class="country-selector__label"
            @click.stop="toggleList">
          </label>
        </div>
      </div>
      <div class="flex-1">
        <InputTel
          :id="`input_phone_number`"
          ref="PhoneNumberInput"
          v-model="phoneNumber"
          :example="example"
          :valid="isValid && !noValidatorState"
          v-bind="$attrs"
          class="input-phone-number"
          :class="{'acceptInp': acceptInput}"
          @keydown="(e) => { lastKeyPressed = e.keyCode }"
          @focus="onFocus"
          @blur="onBlur"/>
      </div>
      <div @mousedown.prevent @click="clearInput">
        <BaseClearInput
          v-if="focusedInput && value"
          tabindex="-1"
          :class="{ 'bigClearInp': inputMode === 'big' }"
          />
      </div>
      <Transition name="slide">
        <div
          v-show="hasListOpen"
          ref="countriesList"
          class="country-selector__list"
          :class="{ 'has-calling-code': showCodeOnList }">
            <RecycleScroller
              v-slot="{ item }"
              :items="countriesSorted"
              :item-size="1"
              key-field="iso2">
              <button
                :key="`item-${item.code}`"
                :class="[ { 'selected': countryCode === item.iso2 }, { 'keyboard-selected': value !== item.iso2 && countryCode === item.iso2 } ]"
                class="flex align-center country-selector__list__item"
                tabindex="-1"
                type="button"
                @click.stop="updateValue(item.iso2, item.dialCode)">
                <div class="country-selector__list__item__flag-container">
                  <div :class="`iti-flag-small iti-flag ${item.iso2.toLowerCase()}`" />
                </div>
                <div class="dots-text">
                  {{ item.name }}
                </div>
                <span class="country-selector__list__item__calling-code flex-fixed">
                  +{{ item.dialCode }}
                </span>
              </button>
            </RecycleScroller>
        </div>
      </Transition>
    </div>
    <div class="error-input-message" v-if="error">
      <slot name="empty-input-error" v-if="!value">
        {{ requireErrorMessage }}
      </slot>
      <slot class="error" name="invalid-input-error" v-if="value">
        {{ invalidErrorMessage }}
      </slot>
    </div>
  </div>
</template>
<script>
import { countries, countriesIso } from './assets/js/phoneCodeCountries.js'
import
{
  parsePhoneNumberFromString,
  AsYouType
} from 'libphonenumber-js/min'
import phoneMetadata from 'libphonenumber-js/metadata.min.json'
import examples from 'libphonenumber-js/examples.mobile.json'
import InputTel from './InputTel'
import { RecycleScroller } from 'vue-virtual-scroller'
import BaseClearInput from '../BaseClearInput.vue'
const isCountryAvailable = (locale) => {
  return countriesIso.includes(locale)
}

export default {
  name: 'BaseInputPhone',
  components: {
    InputTel,
    RecycleScroller,
    BaseClearInput
  },
  props: {
    value: { type: String, default: null },
    id: { type: String, default: 'BaseInputPhone' },
    defaultCountryCode: { type: String, default: null },
    noValidatorState: { type: Boolean, default: false },
    countriesHeight: { type: Number, default: 30 },
    showCodeOnList: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    valid: { type: Boolean, default: false },
    inputMode: { type: String, default: 'big' },
    countryLock: { type: Boolean, default: false },
    excludedCountries: { type: Array, default: () => [] }
  },
  data () {
    return {
      results: {},
      userLocale: this.defaultCountryCode,
      lastKeyPressed: null,
      focusedInput: false,
      isFocus: false,
      hasListOpen: false,
      selectedIndex: null,
      query: '',
      indexItemToShow: 0,
      metadata: phoneMetadata.countries
    }
  },
  computed: {
    filteredCountries () {
      const excludedCountriesLowerCase = this.excludedCountries.map(country => country.toLowerCase())
      return this.excludedCountries?.length === 0
        ? countries
        : countries.filter(country => !excludedCountriesLowerCase?.includes(country.iso2.toLowerCase()))
    },
    countryCode: {
      get () {
        return this.userLocale || this.results.countryCode
      },
      set (newCountry) {
        this.setLocale(newCountry)
      }
    },
    dialCode: {
      get () {
        return this.countryCode
          ? this.getCountryDialCode(this.countryCode)
          : this.getCountryDialCode(this.defaultCountryCode)
      },
      set (newCountry) {
        return newCountry
      }
    },
    phoneNumber: {
      get () {
        return this.value
      },
      set (newPhone) {
        this.emitValues({ countryCode: this.countryCode, phoneNumber: newPhone })
      }
    },
    acceptInput () {
      return this.valid && !this.focusedInput
    },
    calculatedCountryHeight () {
      const height = document.querySelector('.country-selector__list__item')
        .getBoundingClientRect().height
      return height || this.countriesHeight
    },
    phoneFormatted () {
      return this.results.formatInternational
    },
    isValid () {
      return this.results.isValid
    },
    countriesSorted () {
      return this.filteredCountries
    },
    selectedValueIndex () {
      return this.value
        ? this.countriesSorted.findIndex(c => c.iso2 === this.value)
        : null
    },
    tmpValueIndex () {
      return this.countriesSorted.findIndex(c => c.iso2 === this.countryCode)
    },
    callingCode () {
      if (this.isCodeDialRegionOf1) return `+${this.dialCode}`
      return this.countryCode ? `+${this.getCountryDialCode(this.countryCode)}` : null
    },
    example () {
      const res = this.getAsYouTypeFormat({ countryCode: this.countryCode, phoneNumber: examples[this.countryCode] })
      if (this.isCodeDialRegionOf1) return res.slice(6)
      return res
    },
    isCodeDialRegionOf1 () {
      return this.dialCode.length === 4
    },
    requireErrorMessage () {
      return this.$t('qic_error_empty_input')
    },
    invalidErrorMessage () {
      return this.$t('qic_error_invalid_input')
    }
  },
  watch: {
    defaultCountryCode (newValue, oldValue) {
      if (newValue === oldValue) return
      this.setLocale(newValue)
    },
    phoneNumber: {
      handler (newValue, oldValue) {
        // init component (countryCode & phoneNumber) if phone number is provide
        if (newValue && (newValue !== oldValue)) {
          const phoneNumber = parsePhoneNumberFromString(newValue)
          if (phoneNumber) {
            this.emitValues({
              phoneNumber: phoneNumber.nationalNumber,
              countryCode: this.countryCode ? this.countryCode : phoneNumber.country
            })
          }
        }
      },
      immediate: true
    }
  },
  async mounted () {
    if (this.defaultCountryCode) this.emitValues({ countryCode: this.defaultCountryCode, phoneNumber: this.phoneNumber })
  },
  methods: {
    clearInput () {
      this.phoneNumber = ''
    },
    getAsYouTypeFormat (payload) {
      const { countryCode, phoneNumber } = payload
      const asYouType = new AsYouType(countryCode)
      return phoneNumber ? asYouType.input(phoneNumber) : null
    },
    getCountryDialCode (countryCode) {
      try {
        const country = countries.find(c => c.iso2 === countryCode)
        return country.dialCode
      } catch (er) {
        throw new Error(`Ther's no country with ${countryCode} country code`)
      }
    },
    getParsePhoneNumberFromString ({ phoneNumber, countryCode }) {
      const parsing = phoneNumber && countryCode ? parsePhoneNumberFromString(phoneNumber, countryCode) : null
      return {
        countryCode,
        dialCode: this.dialCode,
        phoneLength: function () { return this.phoneExample ? this.phoneExample.length : 11 },
        isValidLength: function () {
          try {
            let formatted = this.e164 ? this.e164.replace('+', '') : ''
            if (this.dialCode.length === 4) {
              formatted = `${this.dialCode}${this.phoneNumber}`.replace(/[-()\s]+/g, '')
            }
            return formatted.length === this.phoneLength()
          } catch {
            return false
          }
        },
        formattedNumber: function () {
          try {
            if (this.dialCode.length === 4) {
              return `+${this.dialCode}${this.phoneNumber}`.replace(/[-()\s]+/g, '')
            }
            return parsing ? parsing.number : ''
          } catch {
            return false
          }
        },
        ...(phoneNumber && (phoneNumber !== '')
          ? { phoneNumber }
          : null
        ),
        ...(parsing
          ? {
              phoneExample: `${parsing.countryCallingCode}${examples[countryCode]}`,
              countryCallingCode: parsing.countryCallingCode,
              nationalNumber: parsing.nationalNumber,
              countryCode: this.countryCode,
              isValid: parsing.isValid(),
              uri: parsing.getURI(),
              e164: parsing.format('E.164')
            }
          : null
        )
      }
    },
    emitValues (payload) {
      const backSpacePressed = this.lastKeyPressed === 8
      this.$nextTick(() => {
        const lastCharacOfPhoneNumber = this.phoneNumber ? this.phoneNumber.trim().slice(-1) : false
        if (backSpacePressed &&
          lastCharacOfPhoneNumber &&
          (lastCharacOfPhoneNumber.slice(-1) === ')' || lastCharacOfPhoneNumber.slice(-1) === '-')) {
          payload.phoneNumber = this.phoneNumber.slice(0, -2)
        }

        this.results = this.getParsePhoneNumberFromString(payload)
        this.$emit('update', this.results)
        this.$emit('input', this.results.phoneNumber)
      })
    },
    setLocale (locale) {
      const countryAvailable = isCountryAvailable(locale)
      if (countryAvailable && locale) {
        this.userLocale = countryAvailable ? locale : null
        this.emitValues({ countryCode: locale, phoneNumber: this.phoneNumber })
      } else if (!countryAvailable && locale) {
        window.console.warn(`The locale ${locale} is not available`)
      }
    },
    onFocus () {
      this.$emit('phone-number-focused')
      this.focusedInput = true
      this.closeList()
    },
    onBlur () {
      this.$emit('phone-number-blur')
      this.focusedInput = false
      this.closeList()
    },
    handleBlur (e) {
      if (this.$el.contains(e.relatedTarget)) return
      this.isFocus = false
      this.closeList()
    },
    toggleList () {
      this.$refs.countriesList.offsetParent ? this.closeList() : this.openList()
    },
    openList () {
      if (this.countryLock) return
      this.$refs.CountrySelector.focus()
      this.$emit('open')
      this.isFocus = true
      this.hasListOpen = true
      if (this.value) this.scrollToSelectedOnFocus(this.selectedValueIndex)
    },
    closeList () {
      this.$emit('close')
      this.hasListOpen = false
    },
    async updateValue (val, dialCode) {
      this.countryCode = val
      this.dialCode = dialCode
      await this.$nextTick()
      this.closeList()
    },
    scrollToSelectedOnFocus (arrayIndex) {
      this.$nextTick(() => {
        this.$refs.countriesList.scrollTop = arrayIndex * (this.calculatedCountryHeight + 1) - ((this.calculatedCountryHeight + 1) * 3)
      })
    },
    keyboardNav (e) {
      const code = e.keyCode
      if (code === 40 || code === 38) {
        // arrow up down
        if (e.view && e.view.event) {
          // TODO : It's not compatible with FireFox
          e.view.event.preventDefault()
        }
        if (!this.hasListOpen) this.openList()
        let index = code === 40 ? this.tmpValueIndex + 1 : this.tmpValueIndex - 1
        if (index === -1 || index >= this.countriesSorted.length) {
          index = index === -1
            ? this.countriesSorted.length - 1
            : 0
        }
        this.countryCode = this.countriesSorted[index].iso2
        this.scrollToSelectedOnFocus(index)
      } else if (code === 13) {
        // enter
        this.hasListOpen ? this.updateValue(this.countryCode) : this.openList()
      } else if (code === 27) {
        // escape
        this.closeList()
      } else {
        this.searching(e)
      }
    },
    searching (e) {
      const code = e.keyCode
      clearTimeout(this.queryTimer)
      this.queryTimer = setTimeout(() => {
        this.query = ''
      }, 1000)
      const q = String.fromCharCode(code)
      if (code === 8 && this.query !== '') {
        this.query = this.query.substring(0, this.query.length - 1)
      } else if (/[a-zA-Z-e ]/.test(q)) {
        if (!this.hasListOpen) this.openList()
        this.query += e.key
        const countries = this.preferredCountries ? this.countriesSorted.slice(this.preferredCountries.length) : this.countriesSorted
        const resultIndex = countries.findIndex(c => {
          this.tmpValue = c.iso2
          return c.name.toLowerCase().startsWith(this.query)
        })
        if (resultIndex !== -1) {
          this.scrollToSelectedOnFocus(resultIndex + (this.preferredCountries ? this.preferredCountries.length : 0))
        }
      }
    }
  }
}
</script>
<style lang="scss" scoped>
  @import './assets/iti-flags/flags.css';
  .country-selector__list {
    &::-webkit-scrollbar-thumb {
      position: absolute;
      background-color: #b3c0c8;
      border-radius: 9em;
    }
    &::-webkit-scrollbar {
    margin-right: 1px;
    max-width: 5px;
    background-color: transparent
}
  }
  .slide-enter-active,
    .slide-leave-active {
      opacity: 1;
      z-index: 998;
      transition: all 0.3s;
      transform: translateY(0);
    }

    .slide-enter,
    .slide-leave-to /* .fade-leave-active below version 2.1.8 */ {
      opacity: 0;
      z-index: 998;
      transform: translateY(-20px);
    }
    .error-input-message {
      color: #ef3030;
      font-size: 12px;
      line-height: 1;
      margin-top: 8px;
    }
</style>
